import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CompatibilityRing from "../components/CompatibilityRing";
import DimensionalRanking from "../components/DimensionalRanking";
import { Sparkles, Heart, X, ArrowLeft, MessageCircle, MessageSquare, Crown, ShieldCheck,
         Instagram, Linkedin, Facebook, Music2, AlertTriangle, ExternalLink, Search,
         CalendarClock, Lock, BookOpen, Plus } from "lucide-react";
import VerifiedBadge, { isVerified } from "../components/VerifiedBadge";
import PremiumBadge from "../components/PremiumBadge";
import AffinityBadge from "../components/AffinityBadge";
import FirstDateQuestions from "../components/FirstDateQuestions";
import AstroSection from "../components/AstroSection";
import EncounterForm from "../components/EncounterForm";
import EncounterCard from "../components/EncounterCard";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const PLACEHOLDER = "https://images.pexels.com/photos/8629727/pexels-photo-8629727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [percentiles, setPercentiles] = useState(null);
  const [research, setResearch] = useState(null);
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [researchError, setResearchError] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [encounterFormOpen, setEncounterFormOpen] = useState(false);
  const [editingEncounter, setEditingEncounter] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [usingToken, setUsingToken] = useState(false);

  const profileUnlockedByToken = !!(tokens && (tokens.unlocked_profiles || []).includes(id));
  const hasFullAccess = !!user?.is_premium || profileUnlockedByToken;

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get(`/profile/${id}`);
        setData(res.data);
      } catch (err) {
        toast.error("Perfil não encontrado.");
        navigate("/discover");
      }
    })();
    // fetch ranking in parallel (non-blocking)
    (async () => {
      try {
        const r = await client.get(`/match-percentiles/${id}`);
        setPercentiles(r.data);
      } catch (err) { console.warn("[match-percentiles]", err); }
    })();
    // fetch token state in parallel
    (async () => {
      try {
        const r = await client.get("/discovery-tokens");
        setTokens(r.data);
      } catch (err) { console.warn("[discovery-tokens]", err); }
    })();
  }, [id, navigate]);

  const useDiscoveryToken = async () => {
    if (usingToken) return;
    const remaining = tokens?.available ?? 0;
    const ok = window.confirm(
      `Usar 1 ficha de descoberta para desbloquear este perfil para sempre?\n\n`
      + `Ficar-te-ão ${Math.max(0, remaining - 1)} ficha(s).\n\n`
      + `Vais ganhar acesso à análise IA completa, perguntas para o 1º encontro `
      + `e pesquisa pública (se a pessoa autorizou).`
    );
    if (!ok) return;
    setUsingToken(true);
    try {
      const r = await client.post("/discovery-tokens/use", { target_user_id: id });
      setTokens((t) => ({ ...(t || {}), available: r.data.available, unlocked_profiles: r.data.unlocked_profiles }));
      toast.success("Ficha aplicada. Perfil desbloqueado para sempre.");
      // Clear stale paywall errors so the user can re-trigger features.
      setResearchError(null);
      // Re-fetch the analysis so the full content replaces the teaser
      try {
        const a = await client.get(`/match-analysis/${id}`);
        setAnalysis(a.data);
      } catch (err) { console.warn("[match-analysis post-token]", err); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao usar a ficha.");
    } finally {
      setUsingToken(false);
    }
  };

  // Load encounters once we know there's a mutual match
  useEffect(() => {
    const matchId = data?.match_id;
    if (!matchId) return;
    (async () => {
      try {
        const r = await client.get(`/encounters?match_id=${matchId}`);
        setEncounters(r.data.encounters || []);
      } catch (err) { console.warn("[encounters fetch]", err); }
    })();
  }, [data?.match_id]);

  // Deep-link ?edit_encounter=ID (from the global /encontros page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eid = params.get("edit_encounter");
    if (!eid || encounters.length === 0) return;
    const target = encounters.find((e) => e.id === eid);
    if (target) { setEditingEncounter(target); setEncounterFormOpen(true); }
  }, [encounters]);

  const onSavedEncounter = (saved) => {
    setEncounters((xs) => {
      const others = xs.filter((x) => x.id !== saved.id);
      return [saved, ...others].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    });
    setEncounterFormOpen(false);
    setEditingEncounter(null);
  };

  const onDeleteEncounter = async (enc) => {
    if (!window.confirm("Apagar este registo? Não pode ser desfeito.")) return;
    try {
      await client.delete(`/encounters/${enc.id}`);
      setEncounters((xs) => xs.filter((x) => x.id !== enc.id));
      toast.success("Registo apagado.");
    } catch { toast.error("Falha ao apagar."); }
  };

  const runAi = async (forceRegenerate = false) => {
    setLoadingAi(true);
    try {
      const url = `/match-analysis/${id}` + (forceRegenerate ? "?force_regenerate=true" : "");
      const res = await client.get(url);
      setAnalysis(res.data);
      if (forceRegenerate) toast.success("Nova análise gerada.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) toast.error("Funcionalidade Premium.");
      else if (status === 429) toast.error(err?.response?.data?.detail || "Limite de re-gerações atingido.");
      else toast.error("Falha na análise IA.");
    } finally { setLoadingAi(false); }
  };

  const runResearch = async () => {
    setLoadingResearch(true);
    setResearchError(null);
    try {
      const res = await client.get(`/research/${id}`);
      setResearch(res.data);
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail || "Falha na pesquisa pública.";
      if (code === 402) setResearchError({ kind: "premium", msg: detail });
      else if (code === 403) setResearchError({ kind: "no_consent", msg: detail });
      else setResearchError({ kind: "error", msg: detail });
    } finally { setLoadingResearch(false); }
  };

  const onLike = async () => {
    try {
      const res = await client.post("/like", { target_user_id: id });
      if (res.data.match) toast.success(`Ligação mútua com ${data.profile.name}!`);
      else toast.success("Gostaste deste perfil.");
      navigate("/discover");
    } catch { toast.error("Falha ao gostar."); }
  };

  const onPass = async () => {
    try { await client.post("/pass", { target_user_id: id }); navigate("/discover"); }
    catch { toast.error("Falha."); }
  };

  if (!data) return (
    <div className="min-h-screen bg-[#0C0E12]"><Navbar /><div className="p-12 text-[#A29F9B]">A carregar...</div></div>
  );

  const p = data.profile;
  const c = data.compatibility;

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="profile-detail-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <button onClick={()=>navigate(-1)} className="text-sm text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-2 mb-8" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        <div className="grid md:grid-cols-2 gap-10">
          <div className="rounded-2xl overflow-hidden border border-white/5 aspect-square md:aspect-[3/4] max-h-[70vh] md:max-h-none">
            <img src={p.photos?.[0] || PLACEHOLDER} alt="" className="w-full h-full object-cover" />
          </div>

          <div>
            <span className="label-uppercase">Perfil</span>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <h1 className="font-display text-3xl md:text-5xl" data-testid="profile-name">
                {p.name}<span className="text-[#C5A880]">, {p.age}</span>
              </h1>
              {isVerified(p) && <VerifiedBadge size="md" />}
              {p.is_premium && <PremiumBadge size="lg" />}
              <AffinityBadge tier={data.tier} score={c.overall} size="lg" />
            </div>
            {p.location && <p className="text-[#A29F9B] mt-1">{p.location}</p>}

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
              <div className="self-center sm:self-auto shrink-0">
                <CompatibilityRing value={c.overall} size={120} />
              </div>
              <div className="space-y-2 w-full min-w-0">
                <Row label="Personalidade" v={c.personality} />
                <Row label="Valores" v={c.values} />
                <Row label="Sonhos & visão" v={c.vision} />
                <Row label="Mental & espiritual" v={c.mental_spiritual} />
                <Row label="Físico" v={c.physical} />
                <Row label="Estilo de vida" v={c.lifestyle} />
              </div>
            </div>

            {p.bio && (
              <div className="mt-8">
                <span className="label-uppercase">Bio</span>
                <p className="mt-2 text-[#DCD7D2] leading-relaxed" data-testid="profile-bio">{p.bio}</p>
              </div>
            )}

            {p.social_profiles && Object.values(p.social_profiles).filter(Boolean).length > 0 && p.is_full_profile && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="profile-socials">
                {[
                  { k: "instagram", icon: Instagram },
                  { k: "linkedin", icon: Linkedin },
                  { k: "facebook", icon: Facebook },
                  { k: "tiktok", icon: Music2 },
                ].filter(({k}) => p.social_profiles?.[k]).map(({k, icon: Icon}) => (
                  <a key={k} href={p.social_profiles[k]} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#161920] border border-white/10 text-[#DCD7D2] hover:border-[#C5A880]/40 hover:text-[#C5A880] text-xs transition-colors"
                    data-testid={`social-link-${k}`}>
                    <Icon className="w-3.5 h-3.5" /> {k}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ))}
              </div>
            )}

            {!p.is_full_profile && (
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-[#A29F9B]" data-testid="privacy-hint">
                <Lock className="w-3.5 h-3.5" /> Perfis sociais e agenda visíveis apenas em ligação mútua
              </div>
            )}

            {p.is_full_profile && p.agenda_url && (
              <div className="mt-6" data-testid="agenda-cta-wrap">
                <a href={p.agenda_url} target="_blank" rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="agenda-cta">
                  <CalendarClock className="w-4 h-4" /> Agendar encontro com {p.name}
                  <ExternalLink className="w-3 h-3 opacity-80" />
                </a>
                <p className="text-xs text-[#A29F9B] mt-2">Abre a agenda pessoal de {p.name} numa nova janela.</p>
              </div>
            )}

            {p.allow_public_research && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/30 text-xs text-[#C5A880]" data-testid="profile-research-badge">
                <ShieldCheck className="w-3.5 h-3.5" /> Verificação pública autorizada
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-4 items-center">
              {data.match_id ? (
                // Already a mutual match → CTA goes to chat
                <Link to={`/chat/${data.match_id}`} className="btn-primary inline-flex items-center gap-2" data-testid="detail-open-chat">
                  <MessageCircle className="w-4 h-4" /> Abrir conversa
                </Link>
              ) : data.my_interaction === "like" ? (
                // I've already liked them, waiting for them to accept
                <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/40 text-sm text-[#C5A880]" data-testid="detail-already-liked">
                  <Heart className="w-4 h-4" /> Convite enviado · à espera de resposta
                </span>
              ) : data.my_interaction === "pass" ? (
                // I've passed — silent, no buttons
                <span className="text-xs text-[#A29F9B]" data-testid="detail-already-passed">Já passaste este perfil.</span>
              ) : (
                // Default: show pass + connect (or "accept" if they liked me first)
                <>
                  <button onClick={onPass} className="btn-secondary inline-flex items-center gap-2" data-testid="detail-pass">
                    <X className="w-4 h-4" /> Passar
                  </button>
                  <button onClick={onLike} className="btn-primary inline-flex items-center gap-2" data-testid="detail-like">
                    <Heart className="w-4 h-4" /> {data.they_liked_me ? "Aceitar convite" : "Conectar"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Dimensional Ranking */}
        {percentiles && percentiles.pool_size > 0 && (
          <div className="mt-16">
            <DimensionalRanking percentiles={percentiles.percentiles} poolSize={percentiles.pool_size} />
          </div>
        )}

        {/* Encounters (dating journal) — only for mutual matches */}
        {data.match_id && (
          <section className="mt-16" data-testid="encounters-section">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#C5A880]" />
                  <span className="label-uppercase">Diário de encontros</span>
                </div>
                <h2 className="font-display text-2xl md:text-3xl mt-2">Memórias com {p.name}</h2>
                <p className="text-sm text-[#A29F9B] mt-2 max-w-xl">
                  Regista cada encontro — privado (só para ti) ou partilhado com {p.name}.
                </p>
              </div>
              {!encounterFormOpen && (
                <button onClick={()=>{ setEditingEncounter(null); setEncounterFormOpen(true); }}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="encounter-new">
                  <Plus className="w-4 h-4" /> Registar encontro
                </button>
              )}
            </div>

            {encounterFormOpen && (
              <div className="mb-8">
                <EncounterForm
                  matchId={data.match_id}
                  initial={editingEncounter}
                  onSaved={onSavedEncounter}
                  onCancel={()=>{ setEncounterFormOpen(false); setEditingEncounter(null); }}
                />
              </div>
            )}

            {encounters.length === 0 && !encounterFormOpen && (
              <div className="bg-[#161920] border border-white/5 rounded-2xl p-10 text-center" data-testid="encounters-empty-inline">
                <p className="text-[#A29F9B]">Ainda sem memórias registadas. Regista o primeiro encontro.</p>
              </div>
            )}

            {encounters.length > 0 && (
              <div className="space-y-4" data-testid="encounters-list-inline">
                {encounters.map((e) => (
                  <EncounterCard key={e.id} encounter={e}
                    onEdit={(enc)=>{ setEditingEncounter(enc); setEncounterFormOpen(true); window.scrollTo({top: 0, behavior: 'smooth'}); }}
                    onDelete={onDeleteEncounter} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* First-date validation questions (Premium, mutual matches only) */}
        <FirstDateQuestions
          targetUserId={id}
          isPremium={hasFullAccess}
          hasMutualMatch={!!data.match_id}
        />

        {/* Astrology — western sun + ascendant + Balinese Pawukon */}
        <AstroSection targetUserId={id} isSelf={false} />

        {/* Public Presence Research */}
        {p.allow_public_research && (
          <div className="mt-16 bg-[#161920] border border-white/5 rounded-2xl p-8 md:p-12" data-testid="research-section">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#C5A880]" />
                  <span className="label-uppercase">Análise de presença pública</span>
                </div>
                <h2 className="font-display text-2xl md:text-3xl mt-2">O que diz a internet sobre {p.name}</h2>
                <p className="text-sm text-[#A29F9B] mt-2 max-w-xl">
                  Síntese de informação publicamente indexada — autorizada por {p.name}. Premium.
                </p>
              </div>
              {!research && (
                <button onClick={runResearch} disabled={loadingResearch}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="run-research">
                  <Search className="w-4 h-4" />
                  {loadingResearch ? "A pesquisar..." : "Gerar análise"}
                </button>
              )}
            </div>

            {loadingResearch && <div className="h-2 rounded-full shimmer bg-white/5" data-testid="research-loading" />}

            {researchError && researchError.kind === "premium" && (
              <div className="text-center py-8" data-testid="research-paywall">
                <Crown className="w-10 h-10 text-[#C5A880] mx-auto mb-4" />
                <h3 className="font-display text-2xl mb-2">Análise pública é Premium</h3>
                <p className="text-[#A29F9B] mb-6">
                  {(tokens?.available ?? 0) > 0
                    ? "Subscreve Premium ou usa 1 ficha de descoberta para desbloquear este perfil."
                    : "Subscreve Premium para desbloquear a verificação pública."}
                </p>
                <div className="flex gap-2 justify-center flex-wrap">
                  {(tokens?.available ?? 0) > 0 && (
                    <button onClick={useDiscoveryToken} disabled={usingToken}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                      data-testid="use-token-research">
                      <Sparkles className="w-4 h-4" />
                      {usingToken ? "A aplicar..." : `Usar 1 ficha (${tokens.available} disp.)`}
                    </button>
                  )}
                  <Link to="/premium" className="btn-secondary inline-block">Subscrever Premium</Link>
                </div>
              </div>
            )}

            {researchError && researchError.kind !== "premium" && (
              <p className="text-sm text-[#7A2021]" data-testid="research-error">{researchError.msg}</p>
            )}

            {research && (
              <div className="space-y-6" data-testid="research-result">
                {!research.available && (
                  <p className="text-[#A29F9B]">Sem material público indexado disponível.</p>
                )}
                {research.summary && (
                  <p className="text-[#DCD7D2] leading-relaxed">{research.summary}</p>
                )}

                {research.alignments?.length > 0 && (
                  <div>
                    <span className="label-uppercase">Sinais de alinhamento</span>
                    <ul className="mt-3 space-y-2">
                      {research.alignments.map((s, i) => (
                        <li key={`align-${i}-${s.slice(0, 24)}`} className="flex gap-3"><span className="text-[#C5A880]">●</span><span>{s}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {research.concerns?.length > 0 && (
                  <div>
                    <span className="label-uppercase">Pontos de atenção</span>
                    <ul className="mt-3 space-y-2">
                      {research.concerns.map((s, i) => (
                        <li key={`concern-${i}-${s.slice(0, 24)}`} className="flex gap-3"><AlertTriangle className="w-4 h-4 text-[#7A2021] mt-1 shrink-0" /><span>{s}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {research.sources?.length > 0 && (
                  <div>
                    <span className="label-uppercase">Fontes consultadas</span>
                    <ul className="mt-3 space-y-2">
                      {research.sources.map((src, i) => (
                        <li key={src.url || `src-${i}`} className="text-sm">
                          <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-[#C5A880] hover:underline inline-flex items-center gap-1">
                            {src.title || src.url} <ExternalLink className="w-3 h-3 opacity-70" />
                          </a>
                          <p className="text-xs text-[#A29F9B] mt-0.5 line-clamp-2">{src.snippet}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-xs text-[#A29F9B] italic mt-6 leading-relaxed">{research.disclaimer}</p>
              </div>
            )}
          </div>
        )}

        {/* AI Analysis */}
        <div className="mt-16 bg-[#161920] border border-white/5 rounded-2xl p-8 md:p-12" data-testid="ai-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="label-uppercase">Análise IA</span>
              <h2 className="font-display text-2xl md:text-3xl mt-2">Compatibilidade interpretada</h2>
            </div>
            {!analysis && (
              <button onClick={() => runAi(false)} disabled={loadingAi} className="btn-primary inline-flex items-center gap-2" data-testid="run-ai">
                <Sparkles className="w-4 h-4" />
                {loadingAi ? "A analisar..." : "Gerar análise"}
              </button>
            )}
            {analysis && analysis.is_premium && (
              <button onClick={() => runAi(true)} disabled={loadingAi}
                className="btn-secondary inline-flex items-center gap-2 text-xs"
                data-testid="regenerate-ai" title="Re-gerar (3/mês — Premium)">
                <Sparkles className={`w-3.5 h-3.5 ${loadingAi ? "animate-spin" : ""}`} />
                Re-gerar análise
              </button>
            )}
          </div>

          {!analysis && !loadingAi && (
            <p className="text-[#A29F9B]">Pede uma análise gerada por Claude — pré-visualização gratuita, detalhe completo é Premium.</p>
          )}

          {loadingAi && <div className="h-2 rounded-full shimmer bg-white/5" data-testid="ai-loading" />}

          {analysis && analysis.is_premium === false && !analysis.unlocked_by_token && (
            <div data-testid="ai-teaser" className="space-y-6">
              <p className="text-[#DCD7D2] leading-relaxed text-lg">
                {analysis.teaser}
              </p>

              {/* Blurred preview */}
              <div className="relative">
                <div className="select-none pointer-events-none filter blur-[6px] opacity-60 space-y-4" aria-hidden="true">
                  <p className="text-[#DCD7D2] leading-relaxed">
                    Ambos partilham um equilíbrio raro entre estabilidade e abertura à aventura,
                    criando espaço para que cada um cresça sem deixar de pertencer ao outro.
                    A forma como comunicam, embora ligeiramente distinta, complementa-se nos
                    momentos de decisão e conflito construtivo. Vejo nesta ligação potencial
                    para uma cumplicidade serena, alimentada por valores familiares próximos
                    e por uma curiosidade mútua pelo mundo.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <span className="label-uppercase">Pontos fortes</span>
                      <ul className="mt-2 space-y-2 text-sm">
                        <li>● Visão alinhada de família</li>
                        <li>● Estilos de afecto compatíveis</li>
                        <li>● Curiosidade partilhada</li>
                      </ul>
                    </div>
                    <div>
                      <span className="label-uppercase">Para quebrar o gelo</span>
                      <ul className="mt-2 space-y-2 text-sm">
                        <li>· Que viagem mudou a tua forma de ver as coisas?</li>
                        <li>· Há algo que faças sempre ao domingo?</li>
                        <li>· Qual a última conversa que te marcou?</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Overlay CTA */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#161920]/80 to-[#161920] flex items-end justify-center pb-8">
                  <div className="text-center max-w-md px-6" data-testid="ai-paywall">
                    <Crown className="w-9 h-9 text-[#C5A880] mx-auto mb-3" />
                    <h3 className="font-display text-2xl mb-2">
                      +{analysis.remaining_words} palavras à tua espera
                    </h3>
                    <p className="text-sm text-[#A29F9B] mb-5">
                      Desbloqueia a análise completa, {analysis.locked_sections?.strengths_count} pontos fortes,
                      {" "}{analysis.locked_sections?.challenges_count} desafios e
                      {" "}{analysis.locked_sections?.icebreakers_count} perguntas para começar a conversa.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
                      {(tokens?.available ?? 0) > 0 ? (
                        <button onClick={useDiscoveryToken} disabled={usingToken}
                          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                          data-testid="use-discovery-token"
                          title="Desbloqueia este perfil para sempre — sem subscrição">
                          <Sparkles className="w-4 h-4" />
                          {usingToken ? "A aplicar..." : `Usar 1 ficha (${tokens.available} disponível${tokens.available === 1 ? "" : "es"})`}
                        </button>
                      ) : null}
                      <Link to="/premium" className="btn-secondary inline-flex items-center gap-2" data-testid="ai-upgrade">
                        <Crown className="w-4 h-4" /> Subscrever Premium
                      </Link>
                    </div>
                    {(tokens?.available ?? 0) === 0 && tokens && (
                      <p className="text-xs text-[#A29F9B]/70 mt-3">
                        Sem fichas. <Link to="/premium#fichas" className="underline hover:text-[#C5A880]">Como ganhar mais →</Link>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {analysis && (analysis.is_premium === true || analysis.unlocked_by_token) && (
            <div className="space-y-8" data-testid="ai-result">
              <p className="text-[#DCD7D2] leading-relaxed whitespace-pre-line">{analysis.analysis}</p>

              {analysis.strengths?.length > 0 && (
                <div>
                  <span className="label-uppercase">Pontos fortes</span>
                  <ul className="mt-3 space-y-2">
                    {analysis.strengths.map((s, i) => (
                      <li key={`str-${i}-${s.slice(0, 24)}`} className="flex gap-3"><span className="text-[#C5A880]">●</span><span>{s}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.challenges?.length > 0 && (
                <div>
                  <span className="label-uppercase">Desafios potenciais</span>
                  <ul className="mt-3 space-y-2">
                    {analysis.challenges.map((s, i) => (
                      <li key={`chall-${i}-${s.slice(0, 24)}`} className="flex gap-3"><span className="text-[#7A2021]">●</span><span>{s}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.icebreakers?.length > 0 && (
                <div>
                  <span className="label-uppercase">Para quebrar o gelo</span>
                  <ul className="mt-3 space-y-2">
                    {analysis.icebreakers.map((s, i) => (
                      <li key={`ice-${i}-${s.slice(0, 24)}`} className="flex gap-3"><MessageSquare className="w-4 h-4 mt-1 text-[#C5A880]" /><span>{s}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Row = ({ label, v }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-[#A29F9B] w-28 shrink-0">{label}</span>
    <div className="flex-1 min-w-[60px] h-1 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full bg-[#C5A880]" style={{ width: `${v}%` }} />
    </div>
    <span className="text-sm gold-text shrink-0 tabular-nums">{v}%</span>
  </div>
);

export default ProfileDetail;
